/**
 * Hook to load real results for template preview
 *
 * Uses the current user's empresa to fetch diagnostic results
 * for the selected questionário, enabling preview with real data
 *
 * If no results are found, returns empty array (mock data will be used)
 */

import { useQuery } from "@tanstack/react-query";
import { getDocs } from "firebase/firestore";
import { getMediaGruposCollectionReference } from "@/modules/media-grupo/firestore";
import useUserStore from "@/modules/users/store";
import { useQuestionarioGroups } from "./useQuestionarioGroups";

const PREVIEW_RESULTADOS_QUERY_KEY = "preview-resultados";

export interface PreviewResultado {
  segment: string;
  value: number;
}

/**
 * Hook para carregar resultados reais para preview de templates
 * @param questionarioId - ID do questionário selecionado
 * @returns Resultados calculados por grupo (ou array vazio se não houver dados)
 */
export function usePreviewResultados(questionarioId?: string) {
  const empresaProjetoId = useUserStore((state) => state.activeEmpresaId);
  const { grupos, isLoading: isLoadingGrupos } = useQuestionarioGroups(questionarioId);

  const resultadosQuery = useQuery({
    queryKey: [PREVIEW_RESULTADOS_QUERY_KEY, empresaProjetoId, questionarioId],
    queryFn: async (): Promise<PreviewResultado[]> => {
      if (!empresaProjetoId || !questionarioId || grupos.length === 0) {
        return [];
      }

      try {
        // Fetch all notas from mediaGrupos collection
        const mediaGruposReference = getMediaGruposCollectionReference(empresaProjetoId, questionarioId);
        const snapshot = await getDocs(mediaGruposReference);

        if (snapshot.docs.length === 0) {
          return [];
        }

        // Group notas by grupoId
        const notasByGrupo = new Map<string, TMediaGrupo[]>();
        for (const document_ of snapshot.docs) {
          const nota = document_.data();
          const notas = notasByGrupo.get(nota.grupoId) || [];
          notas.push(nota);
          notasByGrupo.set(nota.grupoId, notas);
        }

        // Calculate average for each grupo
        // Uses pre-calculated values from Firestore: mediaAvaliador or media
        const resultados: PreviewResultado[] = [];
        for (const grupo of grupos) {
          const notasOfGrupo = notasByGrupo.get(grupo.id) || [];

          if (notasOfGrupo.length === 0) {
            resultados.push({ segment: grupo.nome, value: 0 });
            continue;
          }

          // Use pre-calculated values from Firestore (already in 0-100 scale)
          // Priority: mediaAvaliador (evaluator) > media (auto-evaluation)
          let totalValue = 0;
          let validCount = 0;

          for (const nota of notasOfGrupo) {
            if (nota.mediaAvaliador !== undefined && nota.mediaAvaliador > 0) {
              totalValue += nota.mediaAvaliador;
              validCount++;
            } else if (nota.media !== undefined) {
              totalValue += nota.media;
              validCount++;
            }
          }

          const value = validCount > 0 ? Math.max(0, totalValue / validCount) : 0;
          resultados.push({ segment: grupo.nome, value });
        }

        return resultados;
      } catch (error) {
        console.error("[usePreviewResultados] Error fetching results:", error);
        return [];
      }
    },
    enabled: !!empresaProjetoId && !!questionarioId && grupos.length > 0 && !isLoadingGrupos,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1
  });

  return {
    resultados: resultadosQuery.data ?? [],
    isLoading: resultadosQuery.isLoading || isLoadingGrupos,
    hasRealData: (resultadosQuery.data?.length ?? 0) > 0,
    error: resultadosQuery.error
  };
}
